/**
 * Character Governance Validator
 * 
 * date_of_change: 2026-05-03
 * rule_reference: D&D 5e Character Data Governance Standards (docs/DND_CHARACTER_GOVERNANCE.md)
 * 
 * Implements a 4-stage pipeline with retry-limited auto-correction:
 * 1. Parse & Map
 * 2. Canonicalize
 * 3. Validate (this module)
 * 4. Suggest
 * 
 * Max 2 correction passes to prevent infinite loops.
 */

const SEVERITY = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

const CONFIDENCE = {
  HIGH: 'high',      // Auto-apply, no confirmation
  MEDIUM: 'medium',  // Pre-selected, user can uncheck
  LOW: 'low'         // Flag for review
};

const collectGovernanceWeapons = (character) => [
  character?.equipment?.weapons,
  character?.equipmentDetailed?.weapons,
  character?.weapons,
  character?.equippedWeapons,
  character?.inventory?.equippedWeapons
].flatMap((value) => Array.isArray(value) ? value : []);

const collectGovernanceSpells = (character) => [
  character?.spells?.entries,
  character?.spells?.known,
  character?.spells?.prepared,
  character?.spellcasting?.spells
].flatMap((value) => Array.isArray(value) ? value : []);

const numericRange = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const match = value.match(/\d+/);
    return match ? Number(match[0]) : null;
  }
  if (value && typeof value === 'object') return numericRange(value.normal ?? value.reach ?? value.value);
  return null;
};

/**
 * Governance Rule Definitions
 * Each rule has a validation function and optional correction function
 */
const GOVERNANCE_RULES = {
  // Identity Rules
  'identity.name.required': {
    severity: SEVERITY.ERROR,
    validate: (char) => char.name && String(char.name).trim().length > 0,
    message: 'Character name is required'
  },
  
  'identity.name.length': {
    severity: SEVERITY.WARNING,
    validate: (char) => !char.name || String(char.name).length <= 100,
    message: 'Character name exceeds 100 characters (may be truncated)',
    correct: (char) => {
      if (char.name && String(char.name).length > 100) {
        return { 
          path: 'name', 
          original: char.name, 
          corrected: String(char.name).substring(0, 100),
          confidence: CONFIDENCE.HIGH
        };
      }
      return null;
    }
  },
  
  'identity.level.positive': {
    severity: SEVERITY.ERROR,
    validate: (char) => typeof char.level === 'number' && char.level >= 1 && char.level <= 20,
    message: 'Level must be an integer between 1 and 20'
  },
  
  'identity.level.sumMismatch': {
    severity: SEVERITY.WARNING,
    validate: (char) => {
      if (!Array.isArray(char.classes)) return true;
      const sum = char.classes.reduce((acc, c) => acc + (c.level || 0), 0);
      return sum === 0 || sum === char.level;
    },
    message: 'Level does not match sum of class levels',
    correct: (char) => {
      if (!Array.isArray(char.classes)) return null;
      const sum = char.classes.reduce((acc, c) => acc + (c.level || 0), 0);
      if (sum > 0 && sum !== char.level) {
        return {
          path: 'level',
          original: char.level,
          corrected: sum,
          confidence: CONFIDENCE.HIGH,
          reason: 'Corrected to match sum of class levels'
        };
      }
      return null;
    }
  },
  
  // Combat Stats Rules
  'combat.maxHp.required': {
    severity: SEVERITY.ERROR,
    validate: (char) => char.maxHp !== undefined && char.maxHp !== null && !isNaN(char.maxHp),
    message: 'Maximum HP (maxHp) is required and must be numeric'
  },
  
  'combat.hp.positive': {
    severity: SEVERITY.WARNING,
    validate: (char) => !char.hp || (typeof char.hp === 'number' && char.hp > 0),
    message: 'HP should be a positive number'
  },
  
  // Equipment Rules
  'equipment.numericKeyed': {
    severity: SEVERITY.INFO,
    validate: (char) => {
      const eq = char.equipment;
      if (!eq || typeof eq !== 'object' || Array.isArray(eq)) return true;
      // Check if keys are numeric strings
      const keys = Object.keys(eq);
      return keys.length === 0 || !keys.every(k => /^\d+$/.test(k));
    },
    message: 'Equipment was provided as numeric-keyed object (auto-normalized)',
    correct: (char) => {
      const eq = char.equipment;
      if (!eq || typeof eq !== 'object' || Array.isArray(eq)) return null;
      const keys = Object.keys(eq);
      if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
        return {
          path: 'equipment',
          original: eq,
          corrected: { items: Object.values(eq).map(name => ({ name, type: 'item' })) },
          confidence: CONFIDENCE.HIGH,
          reason: 'Converted numeric-keyed object to canonical equipment.items array'
        };
      }
      return null;
    }
  },
  
  'equipment.proseFilter': {
    severity: SEVERITY.WARNING,
    validate: (char) => {
      if (!char.equipment?.items) return true;
      return !char.equipment.items.some(item => {
        const name = item.name || item;
        const text = String(name).trim();
        return text.length > 80 && text.split(/\s+/).length > 10 && !/\d+\s*(lb|lbs|kg|gp|sp|cp|pp)/i.test(text);
      });
    },
    message: 'Equipment contains narrative prose entries (sentences detected)',
    correct: (char) => {
      if (!char.equipment?.items) return null;
      const proseItems = [];
      const validItems = char.equipment.items.filter(item => {
        const name = item.name || item;
        const text = String(name).trim();
        const isProse = text.length > 80 && text.split(/\s+/).length > 10 && !/\d+\s*(lb|lbs|kg|gp|sp|cp|pp)/i.test(text);
        if (isProse) proseItems.push(item);
        return !isProse;
      });
      if (proseItems.length > 0) {
        return {
          path: 'equipment.items',
          original: char.equipment.items,
          corrected: validItems,
          confidence: CONFIDENCE.HIGH,
          reason: `Filtered ${proseItems.length} prose entries from equipment`
        };
      }
      return null;
    }
  },
  
  'equipment.name.length': {
    severity: SEVERITY.WARNING,
    validate: (char) => {
      if (!char.equipment?.items) return true;
      return char.equipment.items.every(item => {
        const name = item.name || item;
        return String(name).length <= 80;
      });
    },
    message: 'Equipment item names exceed 80 characters (likely prose)'
  },

  'equipment.weaponRange.positive': {
    severity: SEVERITY.WARNING,
    validate: (char) => collectGovernanceWeapons(char).every((weapon) => {
      const range = numericRange(weapon?.range ?? weapon?.attack?.range);
      const reach = numericRange(weapon?.reach ?? weapon?.attack?.reach);
      return range !== 0 && reach !== 0;
    }),
    message: 'Equipped weapons cannot use zero range or zero reach; record a positive reach for melee weapons and range bands for ranged weapons.'
  },

  'spells.mechanics.complete': {
    severity: SEVERITY.WARNING,
    validate: (char) => collectGovernanceSpells(char).every((spell) => {
      if (!spell || typeof spell !== 'object') return true;
      const description = String(spell.description || spell.text || '');
      const damaging = Boolean(spell.damage) || /\b\d+d\d+\b.*\bdamage\b/i.test(description);
      const hasResolution = spell.requiresAttackRoll === true
        || Boolean(spell.attack || spell.attackBonus != null || spell.save || spell.savingThrow || spell.saveType);
      const hasCoreFields = Boolean(spell.castingTime && spell.range && spell.duration);
      return (!damaging || hasResolution) && hasCoreFields;
    }),
    message: 'Canonical spells need casting time, range, duration, and an attack or saving-throw mechanic when they deal damage.'
  },
  
  // Action Rules
  'actions.disqualifiedNames': {
    severity: SEVERITY.WARNING,
    validate: (char) => {
      if (!char.actions) return true;
      const disqualified = new Set([
        'strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma',
        'str', 'dex', 'con', 'int', 'wis', 'cha',
        'saving throws', 'saving throw', 'spellcasting', 'skills', 'senses',
        'languages', 'challenge', 'proficiency bonus', 'passive perception'
      ]);
      return !char.actions.some(a => disqualified.has(String(a.name).toLowerCase().trim()));
    },
    message: 'Actions contain disqualified entries (ability scores, meta-sections)',
    correct: (char) => {
      if (!char.actions) return null;
      const disqualified = new Set([
        'strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma',
        'str', 'dex', 'con', 'int', 'wis', 'cha',
        'saving throws', 'saving throw', 'spellcasting', 'skills', 'senses',
        'languages', 'challenge', 'proficiency bonus', 'passive perception'
      ]);
      const invalidActions = [];
      const validActions = char.actions.filter(a => {
        const isInvalid = disqualified.has(String(a.name).toLowerCase().trim());
        if (isInvalid) invalidActions.push(a);
        return !isInvalid;
      });
      if (invalidActions.length > 0) {
        return {
          path: 'actions',
          original: char.actions,
          corrected: validActions,
          confidence: CONFIDENCE.HIGH,
          reason: `Filtered ${invalidActions.length} disqualified action entries`
        };
      }
      return null;
    }
  },
  
  'actions.sentenceNames': {
    severity: SEVERITY.WARNING,
    validate: (char) => {
      if (!char.actions) return true;
      return char.actions.every(a => {
        const name = String(a.name || '');
        const words = name.trim().split(/\s+/);
        return words.length <= 7;
      });
    },
    message: 'Action names contain sentences (>7 words)',
    correct: (char) => {
      if (!char.actions) return null;
      let corrected = false;
      const newActions = char.actions.map(a => {
        const name = String(a.name || '');
        const words = name.trim().split(/\s+/);
        if (words.length > 7) {
          corrected = true;
          return { ...a, name: words.slice(0, 5).join(' ') + '...' };
        }
        return a;
      });
      if (corrected) {
        return {
          path: 'actions',
          original: char.actions,
          corrected: newActions,
          confidence: CONFIDENCE.MEDIUM,
          reason: 'Truncated sentence-like action names to 5 words'
        };
      }
      return null;
    }
  },
  
  // Class Level Rules
  'classLevels.stringLevels': {
    severity: SEVERITY.INFO,
    validate: (char) => {
      if (!Array.isArray(char.classes)) return true;
      return char.classes.every(c => typeof c.level === 'number');
    },
    message: 'Class levels contain string values (auto-coerced to integers)',
    correct: (char) => {
      if (!Array.isArray(char.classes)) return null;
      let corrected = false;
      const newClasses = char.classes.map(c => {
        if (typeof c.level === 'string') {
          corrected = true;
          return { ...c, level: parseInt(c.level, 10) || 0 };
        }
        return c;
      });
      if (corrected) {
        return {
          path: 'classes',
          original: char.classes,
          corrected: newClasses,
          confidence: CONFIDENCE.HIGH,
          reason: 'Coerced string class levels to integers'
        };
      }
      return null;
    }
  },
  
  'classLevels.subclassFallback': {
    severity: SEVERITY.INFO,
    validate: (char) => {
      if (!Array.isArray(char.classes)) return true;
      return char.classes.every(c => 
        !c.subclass || 
        ['subclass', 'archetype', 'path', 'domain', 'tradition', 'primal_path', 'subclass_name'].some(k => c[k])
      );
    },
    message: 'Subclass data may be under alternate key (checking fallbacks)',
    correct: (char) => {
      if (!Array.isArray(char.classes)) return null;
      let corrected = false;
      const newClasses = char.classes.map(c => {
        if (!c.subclass) {
          const alternate = c.archetype || c.path || c.domain || c.tradition || c.primal_path || c.subclass_name;
          if (alternate) {
            corrected = true;
            return { ...c, subclass: alternate };
          }
        }
        return c;
      });
      if (corrected) {
        return {
          path: 'classes',
          original: char.classes,
          corrected: newClasses,
          confidence: CONFIDENCE.HIGH,
          reason: 'Normalized subclass from alternate key (archetype/path/domain/etc)'
        };
      }
      return null;
    }
  }
};

/**
 * Validation Result
 */
class ValidationResult {
  constructor() {
    this.passed = true;
    this.errors = [];
    this.warnings = [];
    this.info = [];
    this.corrections = [];
    this.retryCount = 0;
  }
  
  addIssue(ruleId, severity, message, path = null) {
    const issue = { ruleId, severity, message, path };
    if (severity === SEVERITY.ERROR) {
      this.passed = false;
      this.errors.push(issue);
    } else if (severity === SEVERITY.WARNING) {
      this.warnings.push(issue);
    } else {
      this.info.push(issue);
    }
  }
  
  addCorrection(correction) {
    this.corrections.push(correction);
  }
}

/**
 * Character Governance Validator
 */
class CharacterGovernanceValidator {
  constructor(options = {}) {
    this.maxRetries = options.maxRetries || 2;
    this.rules = options.rules || GOVERNANCE_RULES;
    this.aiValidator = options.aiValidator || null; // Future AI integration
    this.circuitBreaker = new AICircuitBreaker();
  }
  
  /**
   * Main validation entry point
   * Runs validation with retry-limited auto-correction
   * 
   * date_of_change: 2026-05-04
   * rule_reference: Made synchronous since AI stub doesn't do async work.
   * When real AI is implemented, this can become async again.
   */
  validate(character, context = {}) {
    let currentCharacter = { ...character };
    let result = new ValidationResult();
    
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      result.retryCount = attempt;
      
      // Run rule-based validation
      const passResult = this.runValidationPass(currentCharacter);
      
      // Merge results
      result.errors = [...result.errors, ...passResult.errors];
      result.warnings = [...result.warnings, ...passResult.warnings];
      result.info = [...result.info, ...passResult.info];
      
      // Apply high-confidence corrections
      const highConfidenceCorrections = passResult.corrections.filter(
        c => c.confidence === CONFIDENCE.HIGH
      );
      
      if (highConfidenceCorrections.length === 0) {
        // No more auto-fixable issues
        break;
      }
      
      if (attempt >= this.maxRetries) {
        // Max retries reached, keep remaining corrections as suggestions
        result.warnings.push({
          ruleId: 'system.maxRetries',
          severity: SEVERITY.WARNING,
          message: `Auto-correction stopped after ${this.maxRetries} attempts. ${passResult.corrections.length - highConfidenceCorrections.length} issues remain for manual review.`,
          path: null
        });
        break;
      }
      
      // Apply corrections to character
      for (const correction of highConfidenceCorrections) {
        this.applyCorrection(currentCharacter, correction);
        result.addCorrection(correction);
      }
    }
    
    // AI semantic validation (if available and circuit closed)
    // Note: Currently synchronous since AI stub doesn't do async work
    if (this.aiValidator && !this.circuitBreaker.isOpen()) {
      try {
        const semanticErrors = this.aiValidator.validateSemantics(currentCharacter);
        for (const error of semanticErrors) {
          result.addIssue(error.ruleId, error.severity, error.message, error.path);
        }
      } catch (err) {
        this.circuitBreaker.recordFailure();
        console.warn('AI validation failed, using rule-based only:', err.message);
      }
    }
    
    result.finalData = currentCharacter;
    result.passed = !result.errors.some(e => e.severity === SEVERITY.ERROR && !e.recoverable);
    
    return result;
  }
  
  /**
   * Single validation pass
   */
  runValidationPass(character) {
    const result = new ValidationResult();
    
    for (const [ruleId, rule] of Object.entries(this.rules)) {
      const isValid = rule.validate(character);
      
      if (!isValid) {
        result.addIssue(ruleId, rule.severity, rule.message);
        
        // Attempt correction if available
        if (rule.correct) {
          const correction = rule.correct(character);
          if (correction) {
            result.addCorrection(correction);
          }
        }
      }
    }
    
    return result;
  }
  
  /**
   * Apply a correction to the character object
   */
  applyCorrection(character, correction) {
    const parts = correction.path.split('.');
    let target = character;
    
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      // Handle array notation like "equipment.items[0]"
      const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
      if (arrayMatch) {
        const [, key, index] = arrayMatch;
        target = target[key][parseInt(index, 10)];
      } else {
        target = target[part];
      }
      if (!target) return; // Path doesn't exist, skip
    }
    
    const lastPart = parts[parts.length - 1];
    target[lastPart] = correction.corrected;
  }
  
  /**
   * Generate governance report for UI display
   */
  generateReport(result) {
    return {
      summary: {
        passed: result.passed,
        errorCount: result.errors.length,
        warningCount: result.warnings.length,
        infoCount: result.info.length,
        correctionsApplied: result.corrections.length,
        retries: result.retryCount
      },
      errors: result.errors,
      warnings: result.warnings,
      info: result.info,
      corrections: result.corrections,
      // Quick status for UI badge
      status: result.passed 
        ? (result.warnings.length > 0 ? 'warning' : 'success')
        : 'error'
    };
  }
}

/**
 * AI Circuit Breaker
 * Prevents cascading failures when AI service is unavailable
 */
class AICircuitBreaker {
  constructor(options = {}) {
    this.failureCount = 0;
    this.threshold = options.threshold || 3;
    this.cooldownMs = options.cooldownMs || 60000;
    this.lastFailureTime = null;
  }
  
  isOpen() {
    if (this.failureCount >= this.threshold) {
      // Check if cooldown has passed
      if (this.lastFailureTime && Date.now() - this.lastFailureTime > this.cooldownMs) {
        this.failureCount = 0;
        return false;
      }
      return true;
    }
    return false;
  }
  
  recordFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    // Auto-decrement after cooldown
    setTimeout(() => {
      if (this.failureCount > 0) {
        this.failureCount--;
      }
    }, this.cooldownMs);
  }
}

/**
 * AI Validator Interface Stub
 * Future integration point for LLM-based semantic validation
 */
class AIValidatorStub {
  enhanceParsedData(raw) {
    // TODO: Integrate with LLM for semantic understanding
    return raw;
  }
  
  validateSemantics(canonical) {
    // TODO: Use LLM for semantic validation
    // - Detect misclassified biography text
    // - Identify incorrect action type assignments
    // - Validate spell classifications
    return [];
  }
  
  suggestCorrections(errors) {
    // TODO: Use LLM for context-aware suggestions
    return [];
  }
  
  explainSuggestion(suggestion) {
    // TODO: Generate natural language explanations
    return suggestion.preview || '';
  }
}

// Export for testing and integration
export {
  CharacterGovernanceValidator,
  ValidationResult,
  GOVERNANCE_RULES,
  SEVERITY,
  CONFIDENCE,
  AIValidatorStub,
  AICircuitBreaker
};

export default CharacterGovernanceValidator;
